import { LoadingOutlined } from '@ant-design/icons'
import { Form, ModalProps, notification } from 'antd'
import compact from 'lodash-es/compact'
import { FunctionComponent, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { useFetchUserStatusQuery } from '@/api/user-api'
import {
  useLazyFetchKolAnalyzeQuery,
  useLazyFetchKolLinkAnalysisQuery,
  useLazyFetchRelatedBrandsQuery,
} from '@/api/workspace-api/kol-api'
import { BasicModal } from '@/components/common/antd/basic-modal'
import { Duration } from '@/components/common/duration-select'
import useMakeFormItem, {
  platformOrder,
} from '@/components/kol/export/use-make-export-form-item'
import useKolInfo from '@/components/kol/hooks/use-kol-info'
import useIrmContentContext from '@/components/layout/hooks/use-irm-content-context'
import useFormatPrice from '@/hooks/use-format-price'
import useGenerateKolSlide from '@/hooks/use-generate-kol-slide'
import useWorkspaceDollarSign from '@/hooks/use-workspace-dollar-sign'
import { useIntl } from '@/i18n/hooks/use-intl'
import { RelatedKolType } from '@/store/kol-detail'
import { FetchKolResourceBasicRequest } from '@/types/api/kols'
import type { Nullable } from '@/types/nullable'
import type { PriceRange } from '@/types/schema/kol/price-range-schema'
import { ampli } from '@/utils/ampli'
import {
  convertPlatformShortcode,
  PlatformShortcode,
} from '@/utils/convert-platform'
import exportPptx from '@/utils/export-pptx'
import { SupportedPlatformList } from '@/utils/export-pptx/constants'
import KdSlide from '@/utils/export-pptx/types/slide'
import convertFetchKolLinkAnalysisResponseToLinkAnalysis from '@/utils/kol/convert-fetch-kol-link-analysis-response-to-link-analysis'
import { convertKolInfoToKolSchemaModel } from '@/utils/kol/convert-kol-info-to-kol-schema-model'
import logger from '@/utils/logger'
import mapCurrency from '@/utils/map-currency'
import { toVoidFunction } from '@/utils/to-void-function'

interface KolExportModalProps extends Pick<ModalProps, 'open'> {
  onClose: VoidFunction
  kolId: string
  kolDcIdHashed?: string
}

export interface ExportSetting {
  platforms: PlatformShortcode[]
  period: 'three_months' | 'six_months'
}

const KolExportModal: FunctionComponent<KolExportModalProps> = ({
  open,
  kolId,
  kolDcIdHashed,
  onClose,
}) => {
  const irmContentContext = useIrmContentContext()

  const { formatMessage, formatNumber } = useIntl()
  const theme = useTheme()
  const [form] = Form.useForm<ExportSetting>()
  const { formatPriceRange } = useFormatPrice()

  const [exporting, setExporting] = useState(false)

  const makeFormItem = useMakeFormItem(kolId, kolDcIdHashed)
  const { generateKolSlide } = useGenerateKolSlide()
  const { data: userStatus } = useFetchUserStatusQuery()
  const { kolInfo } = useKolInfo(kolId, kolDcIdHashed)
  const workspaceDollarSign = useWorkspaceDollarSign()
  const [fetchKolAnalyze] = useLazyFetchKolAnalyzeQuery()
  const [fetchRelatedBrands] = useLazyFetchRelatedBrandsQuery()
  const [fetchKolLinkAnalysis] = useLazyFetchKolLinkAnalysisQuery()

  const platforms = kolInfo?.kpi
    .map((kpi) => kpi.platform)
    .filter((platform) =>
      SupportedPlatformList.includes(platform as PlatformShortcode),
    )

  const kolSchemaModel = kolInfo
    ? convertKolInfoToKolSchemaModel(kolInfo)
    : undefined

  const getPriceRange = (range?: Nullable<PriceRange>): string => {
    if (!range) {
      return `${workspaceDollarSign} ******`
    }

    return formatPriceRange({
      minPrice: range.min_price,
      maxPrice: range.max_price,
      currency: range.currency,
    })
  }

  const handleExport = async (values: ExportSetting): Promise<void> => {
    if (exporting) {
      return
    }

    const { platforms, period } = values
    const currencyCode = mapCurrency(userStatus?.currentWorkspace?.currencyCode)

    ampli.exportKolData({
      kolId: kolInfo?.id,
      kolName: kolInfo?.name,
      platforms,
      period: [period],
      format: 'ppt',
    })

    if (!kolSchemaModel || !userStatus) {
      return
    }

    setExporting(true)

    const fetchKolResourceBasicRequest: FetchKolResourceBasicRequest = {
      workspaceId: userStatus.currentWorkspaceId,
      radar_uuid: kolId,
      dc_id_hashed: kolDcIdHashed,
    }

    try {
      const fetchKolAnalyzeResponse = await fetchKolAnalyze(
        fetchKolResourceBasicRequest,
      ).unwrap()
      const fetchRelatedBrandsResponse = await fetchRelatedBrands({
        ...fetchKolResourceBasicRequest,
        platform: PlatformShortcode.Instagram,
        kolType: RelatedKolType.Brand,
        scope:
          period === 'three_months' ? Duration.ThreeMonths : Duration.SixMonths,
        perPage: 3,
        page: 1,
      }).unwrap()
      const linkAnalyze = await fetchKolLinkAnalysis(
        fetchKolResourceBasicRequest,
      ).unwrap()
      const slices = await Promise.all(
        platforms
          .sort((a, b) => {
            return platformOrder.indexOf(a) - platformOrder.indexOf(b)
          })
          .map(async (platformShortcode): Promise<KdSlide | undefined> => {
            const platform = convertPlatformShortcode(platformShortcode)
            const kpi = kolSchemaModel?.kpi.find(
              (kpi) => kpi.platform === platformShortcode,
            )

            if (!kpi) {
              return undefined
            }

            const postQuotationRange = getPriceRange(kpi.quotations?.post)
            const videoQuotationRange = getPriceRange(kpi.quotations?.video)

            return await generateKolSlide({
              workspaceId: userStatus.currentWorkspaceId,
              kolInfo: kolSchemaModel,
              kpi,
              platform,
              period,
              kolAnalyze: fetchKolAnalyzeResponse.data,
              linkAnalyze:
                convertFetchKolLinkAnalysisResponseToLinkAnalysis(linkAnalyze),
              mentionedBrands: fetchRelatedBrandsResponse,
              currencyCode,
              postQuotationRange,
              videoQuotationRange,
            })
          }),
      )

      await exportPptx(
        kolSchemaModel.name,
        compact(slices),
        theme,
        formatMessage,
        formatNumber,
      )
    } catch (error) {
      logger.error(error)

      notification.error({
        message: formatMessage({ id: 'general:operation_failure' }),
        placement: 'bottomRight',
      })
    } finally {
      setExporting(false)
    }
  }

  return (
    <Wrapper>
      <BasicModal
        centered
        destroyOnClose
        cancelButtonProps={{ style: { display: 'none' } }}
        getContainer={irmContentContext?.containerRef?.current ?? document.body}
        okButtonProps={{
          icon: exporting ? <LoadingOutlined /> : undefined,
          disabled: exporting,
        }}
        okText={formatMessage({
          id: exporting ? 'general:exporting' : 'project:export_project',
        })}
        open={open}
        title={formatMessage({ id: 'kol:export_modal_title' })}
        width={520}
        onCancel={onClose}
        onOk={(): void => form.submit()}
      >
        <Description>
          {formatMessage({ id: 'kol:export_modal_description' })}
        </Description>
        <Form
          colon={false}
          form={form}
          labelAlign='left'
          layout='vertical'
          onFinish={toVoidFunction(handleExport)}
        >
          <Section>
            {makeFormItem('platforms', true, 'checkbox', platforms)}
          </Section>
          <Section>
            {makeFormItem('period', false, 'radio', 'three_months')}
          </Section>
        </Form>
      </BasicModal>
    </Wrapper>
  )
}

const Description = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`

const Section = styled.div`
  &:not(:last-child) {
    margin: 0 0 22px;
  }
`

const Wrapper = styled.div`
  .ant-modal-footer .ant-btn + .ant-btn:not(.ant-dropdown-trigger) {
    padding: 0 12px;
  }

  ${Description} {
    margin: 0 0 22px 0;
  }
`

export default KolExportModal
